/** @jsxImportSource @emotion/react */

import type { MouseEvent } from "react";
import { useCallback, useMemo } from "react";
import {
  useOptionListSetValue,
  useOptionListSetValues,
  useOptionListValue,
  useOptionListValues,
} from "../OptionList.provider";
import { optionItemContainerCSS } from "./OptionItem.css";
import type {
  OptionItemBaseProps,
  OptionItemCheckBoxProps,
  OptionItemCompositionProps,
} from "./OptionItem.types";
import { OptionItemBaseCore, OptionItemCheckboxCore } from "./variant";

export const OptionItemComposition = ({
  as,
  className,
  sx,
  children,
  isSelected,
  onClick,
  value,
  text,
  isDisabled,
  nativeOnClick,
  native,
}: OptionItemCompositionProps) => {
  const contextValue = useOptionListValue();
  const contextValues = useOptionListValues();
  const setContextValues = useOptionListSetValues();
  const setContextValue = useOptionListSetValue();

  const textValue = value || text;

  const isChecked = useMemo(() => {
    if (!textValue) return false;

    if (contextValues) {
      return contextValues.includes(textValue);
    }
    return contextValue === textValue;
  }, [contextValue, contextValues, textValue]);

  const updateMultiSelectValues = (
    newValue: string,
    storeValues: string[] | undefined,
    setStoreValues: (values: string[]) => void,
  ) => {
    if (storeValues === undefined) {
      setStoreValues([newValue]);
      return;
    }

    if (storeValues.includes(newValue)) {
      const tempList = storeValues;

      const index = tempList.findIndex((oldValues) => oldValues === newValue);
      tempList.splice(index, 1);

      setStoreValues([...tempList]);
      return;
    }

    setStoreValues([...storeValues, newValue]);
  };

  const updateSelectValue = (newValue: string, setStoreValue: (value: string) => void) => {
    setStoreValue(newValue);
  };

  const _onClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>, newValue?: string) => {
      if (!newValue) return;
      event.preventDefault();

      if (contextValues !== null && setContextValues) {
        updateMultiSelectValues(newValue, contextValues, setContextValues);
      } else if (setContextValue) {
        updateSelectValue(newValue, setContextValue);

        return;
      }

      onClick?.(newValue);
      nativeOnClick?.(event);
    },
    [contextValues, nativeOnClick, onClick, setContextValue, setContextValues],
  );

  const Element = as || "li";

  return (
    <Element
      className={className}
      aria-disabled={isDisabled}
      aria-checked={isSelected || isChecked}
      {...native}
    >
      <button
        disabled={isDisabled}
        css={[optionItemContainerCSS, sx]}
        onClick={(event) => _onClick?.(event, textValue)}
      >
        {children}
      </button>
    </Element>
  );
};

export const OptionItem = ({
  text,
  suffix,
  onClickSuffix,
  isDisabled,
  size = "default",
  ...props
}: OptionItemCompositionProps & OptionItemBaseProps) => (
  <OptionItemComposition text={text} isDisabled={isDisabled} {...props}>
    <OptionItemBaseCore
      text={text}
      suffix={suffix}
      isDisabled={isDisabled}
      size={size}
      onClickSuffix={onClickSuffix}
    />
  </OptionItemComposition>
);

export const OptionItemCheckbox = ({
  as,
  className,
  sx,
  isDisabled,
  isSelected,
  onClick,
  value,
  native,
  ...restProps
}: OptionItemCompositionProps & OptionItemCheckBoxProps) => (
  <OptionItemComposition
    as={as}
    native={native}
    className={className}
    sx={sx}
    isDisabled={isDisabled}
    isSelected={isSelected}
    text={restProps.text}
    onClick={onClick}
    value={value}
  >
    <OptionItemCheckboxCore disabled={isDisabled} value={value} {...restProps} />
  </OptionItemComposition>
);
